import {Box, Card, Grid, Section, SectionIntro, Text} from '@primer/react-brand/esm'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'
import {FlexSuiteRiverBreakout} from '../../../components/FlexSuiteRiverBreakout'
import {FlexSuiteRiverBreakoutTabs} from '../../../components/FlexSuiteRiverBreakoutTabs'
import {FlexSuiteTestimonialBanner, FlexSuiteTestimonialQuote} from '../../../components/FlexSuiteTestimonial'
import {renderWithoutParagraphs} from '../../../utils'
import type {IndexSectionProps} from './types'

export function IndexSection({
  sectionIntroHeading,
  sectionIntroLabel,
  sectionIntroDescription,
  sectionIntroLink,
  breakoutVariant,
  featureRiverBreakout,
  featureBreakoutTabs,
  cardsVariant,
  cardsIconItems,
  cardsImageItems,
  indexTestimonialVariant,
  indexTestimonialLink,
  indexTestimonialLogo,
  indexTestimonialQuote,
  indexTestimonialImage,
  indexTestimonialLabel,
  indexTestimonialName,
  indexTestimonialTitle,
}: IndexSectionProps) {
  const breakoutContent = renderBreakoutContent({breakoutVariant, featureRiverBreakout, featureBreakoutTabs})
  const cardsContent = renderCardsContent({cardsVariant, cardsIconItems, cardsImageItems})
  const socialProofContent = renderSocialProofContent({
    indexTestimonialVariant,
    indexTestimonialLink,
    indexTestimonialLogo,
    indexTestimonialQuote,
    indexTestimonialImage,
    indexTestimonialName,
    indexTestimonialTitle,
  })

  const hasIntroContent = Boolean(
    sectionIntroHeading || sectionIntroLabel || sectionIntroDescription || sectionIntroLink,
  )

  if (!hasIntroContent && !breakoutContent && !cardsContent && !socialProofContent) {
    return null
  }

  return (
    <Section fullWidth paddingBlockStart="none" paddingBlockEnd="none">
      {hasIntroContent ? (
        <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingBlockEnd={{narrow: 'spacious', regular: 64}}>
          <Grid>
            <Grid.Column>
              <SectionIntro align="center" fullWidth>
                {sectionIntroLabel ? <SectionIntro.Label>{sectionIntroLabel}</SectionIntro.Label> : null}
                {sectionIntroHeading ? (
                  <SectionIntro.Heading size="3">{renderWithoutParagraphs(sectionIntroHeading)}</SectionIntro.Heading>
                ) : null}
                {sectionIntroDescription ? (
                  <SectionIntro.Description>
                    {renderWithoutParagraphs(sectionIntroDescription)}
                  </SectionIntro.Description>
                ) : null}
                {sectionIntroLink?.fields.href ? (
                  <SectionIntro.Link href={sectionIntroLink.fields.href}>
                    {sectionIntroLink.fields.text}
                  </SectionIntro.Link>
                ) : null}
              </SectionIntro>
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}
      {breakoutContent}
      {cardsContent ? (
        <Box marginBlockStart={48} data-testid="category-index-cards">
          <Grid style={{paddingInline: 0}}>
            <Grid.Column>
              <Box borderColor="muted" borderStyle="solid" borderInlineStartWidth="thin" borderInlineEndWidth="thin">
                <Grid
                  style={{
                    '--brand-Grid-spacing-row': '0',
                    '--brand-Grid-spacing-column-gap': '0',
                  }}
                >
                  {cardsContent}
                </Grid>
              </Box>
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}
      {socialProofContent ? (
        <Box paddingBlockStart="spacious" paddingBlockEnd="spacious" data-testid="category-index-social-proof">
          {indexTestimonialLabel ? (
            <Box marginBlockEnd={16}>
              <Text as="p" size="200" variant="muted" align="center">
                {indexTestimonialLabel}
              </Text>
            </Box>
          ) : null}
          <Grid>
            <Grid.Column>{socialProofContent}</Grid.Column>
          </Grid>
        </Box>
      ) : null}
    </Section>
  )
}

function renderCardsContent({
  cardsVariant,
  cardsIconItems,
  cardsImageItems,
}: Pick<IndexSectionProps, 'cardsVariant' | 'cardsIconItems' | 'cardsImageItems'>) {
  if (cardsVariant === 'Icon Cards' && cardsIconItems?.length) {
    return cardsIconItems.map((item, index) => {
      const Octicon = getPrimerIcon(item.fields.icon)
      const isMiddle = index > 0 && index < cardsIconItems.length - 1
      const isLast = index === cardsIconItems.length - 1

      return (
        <Grid.Column key={item.sys.id} span={{xsmall: 12, medium: 4}}>
          <Box
            padding={{narrow: 32, regular: 48}}
            borderColor="muted"
            borderStyle="solid"
            borderBlockEndWidth={{narrow: isLast ? 'thin' : 'none', regular: 'none'}}
            borderBlockStartWidth={{narrow: 'thin', regular: 'none'}}
            borderInlineEndWidth={{regular: isMiddle ? 'thin' : 'none'}}
            borderInlineStartWidth={{regular: isMiddle ? 'thin' : 'none'}}
            style={{height: '100%'}}
          >
            <Card href={item.fields.href} ctaVariant="arrow" ctaText={item.fields.label}>
              {Octicon ? <Card.Icon icon={<Octicon />} color="default" hasBackground /> : null}
              {item.fields.eyebrow ? <Card.Label>{item.fields.eyebrow}</Card.Label> : null}
              <Card.Heading>
                {renderWithoutParagraphs(item.fields.heading)}
                {item.fields.alt ? <span className="sr-only"> {item.fields.alt}</span> : null}
              </Card.Heading>
              <Card.Description>{renderWithoutParagraphs(item.fields.description)}</Card.Description>
            </Card>
          </Box>
        </Grid.Column>
      )
    })
  }

  if (cardsVariant === 'Image Cards' && cardsImageItems?.length) {
    return cardsImageItems.map((item, index) => {
      const isMiddle = index > 0 && index < cardsImageItems.length - 1
      const isLast = index === cardsImageItems.length - 1

      return (
        <Grid.Column key={item.sys.id} span={{xsmall: 12, medium: 4}}>
          <Box
            padding={{narrow: 32, regular: 48}}
            borderColor="muted"
            borderStyle="solid"
            borderBlockEndWidth={{narrow: isLast ? 'thin' : 'none', regular: 'none'}}
            borderBlockStartWidth={{narrow: 'thin', regular: 'none'}}
            borderInlineEndWidth={{regular: isMiddle ? 'thin' : 'none'}}
            borderInlineStartWidth={{regular: isMiddle ? 'thin' : 'none'}}
            style={{height: '100%'}}
          >
            <Card href={item.fields.href} variant="minimal" fullWidth ctaText={item.fields.label}>
              <Card.Image
                as="picture"
                aspectRatio="16:9"
                src={`${item.fields.image.fields.file.url}?fm=webp`}
                alt={item.fields.image.fields.description ?? ''}
                sources={getImageSources(item.fields.image.fields.file.url, {maxWidth: MAX_CONTENT_WIDTH / 3})}
              />
              <Card.Heading>
                {renderWithoutParagraphs(item.fields.heading)}
                {item.fields.alt ? <span className="sr-only"> {item.fields.alt}</span> : null}
              </Card.Heading>
              <Card.Description>{renderWithoutParagraphs(item.fields.description)}</Card.Description>
            </Card>
          </Box>
        </Grid.Column>
      )
    })
  }

  return null
}

function renderBreakoutContent({
  breakoutVariant,
  featureRiverBreakout,
  featureBreakoutTabs,
}: Pick<IndexSectionProps, 'breakoutVariant' | 'featureRiverBreakout' | 'featureBreakoutTabs'>) {
  if (breakoutVariant === 'Breakout' && featureRiverBreakout) {
    return (
      <Grid>
        <Grid.Column>
          <FlexSuiteRiverBreakout {...featureRiverBreakout.fields} />
        </Grid.Column>
      </Grid>
    )
  }

  if (breakoutVariant === 'Carousel' && featureBreakoutTabs?.length) {
    return (
      <Grid>
        <Grid.Column>
          <FlexSuiteRiverBreakoutTabs items={featureBreakoutTabs} />
        </Grid.Column>
      </Grid>
    )
  }

  return null
}

function renderSocialProofContent({
  indexTestimonialVariant,
  indexTestimonialLink,
  indexTestimonialLogo,
  indexTestimonialQuote,
  indexTestimonialImage,
  indexTestimonialName,
  indexTestimonialTitle,
}: Pick<
  IndexSectionProps,
  | 'indexTestimonialVariant'
  | 'indexTestimonialLink'
  | 'indexTestimonialLogo'
  | 'indexTestimonialQuote'
  | 'indexTestimonialImage'
  | 'indexTestimonialName'
  | 'indexTestimonialTitle'
>) {
  if (!indexTestimonialVariant || !indexTestimonialQuote) {
    return null
  }

  if (indexTestimonialVariant === 'Quote') {
    return (
      <FlexSuiteTestimonialQuote
        quote={indexTestimonialQuote}
        name={indexTestimonialName}
        title={indexTestimonialTitle}
        link={indexTestimonialLink?.fields}
        logo={indexTestimonialLogo}
      />
    )
  }

  if (indexTestimonialVariant === 'Brand Banner') {
    return (
      <FlexSuiteTestimonialBanner
        quote={indexTestimonialQuote}
        link={indexTestimonialLink?.fields}
        logo={indexTestimonialLogo}
        image={indexTestimonialImage}
      />
    )
  }

  return null
}
